// utils/getClientIp.js

import { getStorageAppItem, setStorageAppItem } from 'stores/app';
import dayjs from 'dayjs';
import appService from 'services/appService';
import { generateCode } from 'utils/helpers';
import fetchX from 'utils/fetchX';

const STORAGE_KEY = 'connection';

/**
 * Save IP to localStorage (encrypted) with expiration at end of day
 */
const saveToLocalStorage = async ip => {
  try {
    const passphrase = import.meta?.env?.VITE_CRYPTO_KEY || 'jarvisly-key-word';
    const newIpExpireDate = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const connectionPlaintext = `${ip}|${newIpExpireDate}`;

    const { encrypt } = await import('./crypto');
    const connectionEncrypted = await encrypt(connectionPlaintext, passphrase);

    await setStorageAppItem(STORAGE_KEY, connectionEncrypted);
  } catch (error) {
    console.warn('[getClientIp] Failed to encrypt and save IP:', error);
  }
};

/**
 * Load IP from localStorage (encrypted) and check if still valid
 */
const loadFromLocalStorage = async () => {
  try {
    const passphrase = import.meta?.env?.VITE_CRYPTO_KEY || 'jarvisly-key-word';
    const encryptedConnection = await getStorageAppItem(STORAGE_KEY);

    if (encryptedConnection) {
      const { decrypt } = await import('./crypto');
      const connectionDecrypted = await decrypt(encryptedConnection, passphrase);
      const [cachedIp, ipExpired] = connectionDecrypted?.split('|') || [];

      // Check if cached IP is still valid (before end of day)
      if (cachedIp && ipExpired && dayjs().isBefore(dayjs(ipExpired))) {
        return cachedIp;
      }
    }
  } catch (error) {
    console.warn('[getClientIp] Failed to decrypt cached IP:', error);
  }

  return null;
};

export async function getClientIp() {
  const cachedIp = await loadFromLocalStorage();

  if (cachedIp) {
    return { ip: cachedIp, provider: 'localStorage' };
  }

  const fallbackResults = [];

  // 1. SeeIP (https, ipv4 only - PRIMARY)
  try {
    const res = await fetchX('https://ipv4.seeip.org/jsonip?');
    const data = await res.json();
    if (data.ip) {
      await saveToLocalStorage(data.ip);
      return { ip: data.ip, provider: 'seeip', fallbackResults };
    }
    fallbackResults.push({ provider: 'seeip', error: 'Invalid response' });
  } catch (err) {
    fallbackResults.push({ provider: 'seeip', error: err.message });
  }

  // 2. IPify (https, limitação: 1000/mês no plano gratuito - FALLBACK 1)
  try {
    const res = await fetchX('https://api.ipify.org?format=json');
    const data = await res.json();
    if (data.ip) {
      await saveToLocalStorage(data.ip);
      return { ip: data.ip, provider: 'ipify', fallbackResults };
    }
    fallbackResults.push({ provider: 'ipify', error: 'Invalid response' });
  } catch (err) {
    fallbackResults.push({ provider: 'ipify', error: err.message });
  }

  // 3. ipinfo.io (não precisa token para IP - FALLBACK 2)
  try {
    const res = await fetchX('https://ipinfo.io/json');
    const data = await res.json();
    if (data.ip) {
      await saveToLocalStorage(data.ip);
      return { ip: data.ip, provider: 'ipinfo', fallbackResults };
    }
    fallbackResults.push({ provider: 'ipinfo', error: 'Invalid response' });
  } catch (err) {
    fallbackResults.push({ provider: 'ipinfo', error: err.message });
  }

  // 4. ifconfig.me (retorna plain/text - FALLBACK 3)
  try {
    const res = await fetchX('https://ifconfig.me/ip');
    const ip = await res.text();
    if (ip && ip.length <= 50) {
      await saveToLocalStorage(ip.trim());
      return { ip: ip.trim(), provider: 'ifconfig.me', fallbackResults };
    }
    fallbackResults.push({ provider: 'ifconfig.me', error: 'Invalid response' });
  } catch (err) {
    fallbackResults.push({ provider: 'ifconfig.me', error: err.message });
  }

  return {
    success: false,
    message: 'Falha ao obter IP do cliente.',
    tried: fallbackResults,
  };
}
